import React from "react";
import { decodeTokenService } from "../services/decodeTokenService";
import { dictionary, Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";

const locales: Record<Language, string> = {
  [Language.SPANISH]: "es-ES",
  [Language.ENGLISH]: "en-US",
};

// Utility for formatted date
const getFormattedDate = (language: Language): string => {
  const currentDate = new Date().toLocaleDateString(locales[language], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return currentDate.charAt(0).toUpperCase() + currentDate.slice(1);
};

// Star SVG Component
const Star = ({
  width,
  height,
  top,
  right,
  rotate,
}: {
  width: number;
  height: number;
  top: string;
  right: string;
  rotate?: string;
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 187 147"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute"
    style={{ top, right, transform: `rotate(${rotate || "0deg"})` }}
  >
    <g clipPath="url(#clip0_666_11493)">
      <path
        d="M93.8696 0C96.7314 19.5856 99.8585 38.7529 105.687 57.4457C109.586 69.945 117.206 77.7953 129.755 81.6562C144.321 86.1363 159.153 89.1687 174.17 91.4771C178.422 92.1286 182.699 92.6756 187 93.7132C183.503 94.1957 180.006 94.6381 176.517 95.1609C160.544 97.5659 144.739 100.598 129.281 105.513C117.407 109.293 109.867 116.605 106.056 128.541C101.281 143.493 98.2588 158.776 95.8793 174.235C95.2281 178.458 94.7056 182.705 93.7972 187C93.2666 183.228 92.7602 179.455 92.2055 175.691C89.9064 160.312 86.8436 145.126 82.3981 130.174C78.4832 117.007 70.3961 108.947 57.2525 105.03C42.1475 100.526 26.7853 97.4614 11.2381 95.1529C7.7412 94.6301 4.23625 94.1797 0.0078125 93.6005C10.2976 91.9356 19.8316 90.5441 29.3014 88.8147C40.9739 86.6752 52.5016 83.9244 63.5791 79.4764C71.3125 76.3717 76.6503 71.1837 79.7935 63.4058C84.866 50.8501 87.7118 37.7394 90.0511 24.492C91.3454 17.1887 92.2779 9.82094 93.3872 2.47735C93.5158 1.64889 93.7168 0.828466 93.8856 0L93.8696 0Z"
        fill="url(#paint0_linear_666_11493)"
        fillOpacity="0.11"
      />
    </g>
    <defs>
      <linearGradient
        id="paint0_linear_666_11493"
        x1="93.5038"
        y1="0"
        x2="93.5038"
        y2="187"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="#EDEDED" />
      </linearGradient>
      <clipPath id="clip0_666_11493">
        <rect width="187" height="187" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const Header = () => {
  const userName = decodeTokenService.getNameFromToken() || "";
  const { language } = useAuth();

  const currentDate = new Date();

  // Determine the current hour
  const currentHour = currentDate.getHours();
  let greeting;

  // Set greeting based on the time of day
  if (currentHour < 12) {
    greeting = dictionary["greeting-morning"][language];
  } else if (currentHour < 18) {
    greeting = dictionary["greeting-afternoon"][language];
  } else {
    greeting = dictionary["greeting-night"][language];
  }

  return (
    <div className="relative w-full bg-gradient-to-r from-[#5050EA] to-[#85A2FF] shadow-xl rounded-[16px] text-white overflow-hidden">
      {/* Precise Star Placement */}
      <Star width={130} height={130} top="-40%" right="35%" rotate="0deg" />
      <Star width={190} height={190} top="-62%" right="-5%" rotate="0deg" />
      <Star width={140} height={140} top="2%" right="19%" rotate="0deg" />
      <Star width={60} height={60} top="58%" right="10%" rotate="0deg" />

      {/* Date */}
      <div className="relative z-10 text-left p-4 ">
        <div className="text-lg text-white/75 mb-1 pl-4">
          {getFormattedDate(language)}
        </div>
        <h2 className="text-[40px] font-extrabold leading-[48px] font-primary pl-4">
          {greeting}
        </h2>
      </div>
    </div>
  );
};

export default Header;
